import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useGptStream } from '../useGptStream';
import { Sender, Bubble, Prompts } from '@ant-design/x';
import { Space, Row, Col, Typography, Divider, Empty } from 'antd';
import { TextQuote } from 'lucide-react';
import { MarkdownStyles } from '../../../utils/markdownUtils';
import Blurb from './components/Blurb';
import ChatList from './components/ChatList';
import ChatTools from './components/ChatTools';
import {
  setActiveConversation,
  setConversationTitle,
    selectConversationById
} from '../../../store/slices/gpt.slice';
import {getContextPrompts} from "./promptConfig";
// Subcomponents for better organization
const Header = () => (
  <Sender.Header
    open={true}
    title={
      <Space>
        <Typography.Text className="!font-medium" type="secondary">
          Ask anything you want about your Portfolio
        </Typography.Text>
        <Typography.Text className="ml-3" type="secondary">
          Press Keys [ Shift + Enter ] at same time to go to a new line
        </Typography.Text>
      </Space>
    }
  />
);

const ConversationHistory = ({ messages, conversations, activeId, onSetActive, onRename }) => (
  <div>
    <div className="component-title flex justify-between text-gray-600 p-3 text-sm font-medium border-b border-gray-200">
      Most Recent
      <TextQuote size={18} />
    </div>
    <ChatList
      conversations={conversations}
      onSetActiveConversation={onSetActive}
      activeId={activeId}
      onRenameConversation={onRename}
    />
  </div>
);

const MessageArea = ({
  messages,
  error,
  isStreaming,
  userPrompt,
  setUserPrompt,
  handleSendMessage,
    conversationId
}) => (
  <div className="mx-auto w-4/5 max-w-[80vw] flex-grow overflow-auto px-8">
    {messages.length === 0 ? (
      <IdeasSection
        setUserPrompt={setUserPrompt}
        handleSendMessage={handleSendMessage}
      />
    ) : (
      <MessagesSection
        messages={messages}
        error={error}
        isStreaming={isStreaming}
        conversationId={conversationId}

      />
    )}
    <InputSection
      userPrompt={userPrompt}
      setUserPrompt={setUserPrompt}
      handleSendMessage={handleSendMessage}
      isStreaming={isStreaming}
    />
  </div>
);

const IdeasSection = ({ setUserPrompt, handleSendMessage }) => {
  const selectedPortfolioId = useSelector((state) => state.gptApi.selectedPortfolioId);
  const portfolioDocument = useSelector((state) =>
    state.portfolios.items?.find((p) => p.id === selectedPortfolioId)
  );

  const contextPrompts = getContextPrompts(selectedPortfolioId);

  return (
    <div className="mt-8">
      <div className="mb-6">
        <Typography.Title level={3}>
          {selectedPortfolioId
            ? `Ideas for ${portfolioDocument?.name || 'Your Portfolio'}`
            : 'General Market Ideas'}
        </Typography.Title>
        {selectedPortfolioId && (
          <Typography.Text type="secondary" className="mt-2 block">
            Ask specific questions about your portfolio's performance, allocation, and strategy
          </Typography.Text>
        )}
      </div>
      <Prompts
        styles={{
          item: {
            flex: 'none',
            width: 'calc(50% - 62px)',
            padding: '16px 24px',
            margin: '16px 12px',
            borderRadius: '8px',
          },
        }}
        wrap={true}
        items={contextPrompts}
        onItemClick={(info) => {
            console.log(info);
          if (!info.data.prompt) return;
          setUserPrompt(info.data.prompt);
          handleSendMessage();
        }}
      />
    </div>
  );
};
const MessagesSection = ({ messages, error, isStreaming, conversationId }) => (
  <>
    {error && (
      <Bubble
        placement="start"
        variant="filled"
        shape="round"
        className="mb-4"
      >
        <strong className="text-red-500">Error:</strong> {error}
      </Bubble>
    )}
    {isStreaming && (
      <Bubble
        placement="start"
        variant="filled"
        shape="round"
        className="mb-4"
        loading
      >
        <span>Loading...</span>
      </Bubble>
    )}
    <div className="markdown-content max-h-[60vh] overflow-y-scroll">
      {messages.map((msg, idx) => (
        <Blurb
          key={idx}
          role={msg.role}
          content={msg.content}
          messageId={msg.messageId}
          conversationId={conversationId}
        />
      ))}
    </div>
  </>
);
const InputSection = ({ userPrompt, setUserPrompt, handleSendMessage, isStreaming }) => (
  <div className="absolute bottom-14 bg-white border border-gray-300 rounded-xl w-2/3 mx-auto">
    <Sender
      header={<Header />}
      value={userPrompt}
      onChange={setUserPrompt}
      onSubmit={handleSendMessage}
      placeholder="Type your question here..."
      disabled={isStreaming}
      allowSpeech={true}
    />
  </div>
);

// Main component
const ChatBox = ({ defaultPromptHeadKey = null, defaultGroupId = null }) => {
  const dispatch = useDispatch();
  const [responseId] = useState(uuidv4());
  const [userPrompt, setUserPrompt] = useState('');
  const activeConversationId = useSelector((state) => state.gptApi.activeConversationId);

  // Get the active conversation's messages
  const activeConversation = useSelector((state) =>
    activeConversationId ? selectConversationById(state, activeConversationId) : { messages: [] }
  );

  const {
    messages,
    isStreaming,
    error,
    startStream,
    allConversations,
    handleSetConversationTitle,
    clearConversation,
  } = useGptStream({
    responseId: activeConversationId || responseId, // Use active conversation ID if available
    initialPromptData: '',
    promptHeadKey: defaultPromptHeadKey,
    groupId: defaultGroupId,
    clearOnUnmount: false // Changed to false to preserve conversation history
  });

  // Use the active conversation's messages instead of the local messages state
  const currentMessages = activeConversation.messages || [];

  const handleSendMessage = () => {
    if (!userPrompt.trim()) return;
    startStream(userPrompt);
    setUserPrompt('');
  };

  const handleSetActiveConversation = (id) => {
    dispatch(setActiveConversation(id));
  };

  const handleRenameConversation = (newTitle) => {
    handleSetConversationTitle(newTitle);
    dispatch(setConversationTitle({ id: activeConversationId, title: newTitle }));
  };

  return (
    <div className="h-[95vh] overflow-hidden">
      <style>{MarkdownStyles}</style>
      <Row wrap={false} className="h-full overflow-hidden">
        <Col flex="0 0 250px" className="h-full border-r-2 border-gray-100 overflow-auto">
            <ConversationHistory
              messages={currentMessages}
              conversations={allConversations}
              activeId={activeConversationId}
              onSetActive={handleSetActiveConversation}
              onRename={handleRenameConversation}
            />

        </Col>

        <Col flex="1" className="h-full flex flex-col">
          <div className="px-6 py-1">
            <ChatTools
              responseId={activeConversationId || responseId}
              onRenameConversation={handleRenameConversation}
              onClearConversation={clearConversation}
            />
          </div>
          <Divider />

          <MessageArea
            messages={currentMessages}
            error={error}
            isStreaming={isStreaming}
            userPrompt={userPrompt}
            setUserPrompt={setUserPrompt}
            handleSendMessage={handleSendMessage}
            conversationId={activeConversationId}
          />
        </Col>
      </Row>
    </div>
  );
};
export default ChatBox;